import React, { useContext } from 'react';
import SelectTrainingSetWithSearch from '../extractionBatches/create/SelectTrainingSetWithSearch';
import ValidTextFieldEB from '../extractionBatches/create/ValidTextFieldEB';
import errorResponses from '../../errorResponses';
import usePaginatedExtractionBatches from '../hooks/usePaginatedExtractionBatches';
import { AppContext } from '../../App';
import { DocumentDispatchContext, DocumentStateContext } from '../../context/DocumentContext';
import { ITrainingSet } from '../../types/my-types';
import { useCreateExtractionBatch } from '../hooks/useCreateExtractionBatch';
import { useLocation } from 'react-router';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { FormLabelStyled, FormControlStyled } from '../common/Dialog/DialogGlynt';

const DocumentTableCreateEB: React.FC = () => {
  const { setToast } = React.useContext(AppContext);
  const { createExtractionBatchModal, extractionBatchLabel, selectedTrainingSet } =
    React.useContext(DocumentStateContext);

  const dispatch = useContext<React.Dispatch<any>>(DocumentDispatchContext);

  const { data } = usePaginatedExtractionBatches({
    limit: 150,
  });

  const { mutateAsync: createExtractionBatch, isPending: isLoadingCreateExtractionBatch } =
    useCreateExtractionBatch();

  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const setLabelEB = (label: string) => {
    dispatch({ type: 'SET_EXTRACTION_BATCH_LABEL', label });
  };

  const setSelectedTrainingSet = (trainingSet: ITrainingSet | undefined) => {
    dispatch({ type: 'SET_SELECTED_TRAINING_SET', trainingSet });
  };

  const closeModal = () => {
    dispatch({ type: 'TOGGLE_CREATE_EXTRACTION_BATCH_MODAL' });
  };

  const handleSubmit = async () => {
    if (selectedTrainingSet?.id) {
      try {
        await createExtractionBatch({
          label: extractionBatchLabel,
          documents: selectedIds,
          training_set: selectedTrainingSet.id,
        });

        closeModal();
        setToast({
          open: true,
          variant: 'success',
          message: 'EB created successfully!',
        });
      } catch (e) {
        setToast({
          open: true,
          variant: 'error',
          message: errorResponses.failedToCreateExtractionBatch,
        });
      }
    }
  };

  const isLoading = isLoadingCreateExtractionBatch;
  const dataReady = !!data?.results;

  return (
    <DialogGlynt
      open={createExtractionBatchModal}
      closeModal={closeModal}
      cypressId="create-extraction-batch-dialog"
      loading={isLoading}
      width="500px"
    >
      <DialogTitleGlynt title="Create New Extraction Batch" closeModal={closeModal} loading={isLoading} />
      <DialogContentGlynt>
        <FormControlStyled data-cy="modal-extraction-batch-name">
          <FormLabelStyled>Batch Name</FormLabelStyled>
          <ValidTextFieldEB
            placeholder="Extraction Batch Name"
            setLabel={setLabelEB}
            disabled={isLoading || !dataReady}
            labels={data?.results?.map((eb: any) => eb.label) || []}
          />
        </FormControlStyled>
        <FormControlStyled data-cy="modal-select-training-set">
          <FormLabelStyled>Model Name</FormLabelStyled>
          <SelectTrainingSetWithSearch
            setTrainingSet={setSelectedTrainingSet}
            disabled={isLoading || !dataReady}
          />
        </FormControlStyled>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        cancelCypressId="create-extraction-batch-dialog-cancel"
        submitCypressId="create-extraction-batch-dialog-next"
        clearCypressId=""
        submitButtonText="Create"
        submitDisabled={!extractionBatchLabel || !selectedTrainingSet?.id || !dataReady}
        submitLoading={isLoading}
      />
    </DialogGlynt>
  );
};

export default DocumentTableCreateEB;
